import { ActionIcon, Button, TextInput } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { AddressAutocomplete } from 'src/Components/Shared/AddressAutocomplete';
import { ImageDropzone } from 'src/Components/Shared/ImageDropzone';
import { useUpdateAsset } from 'src/Hooks/Mutations/useUpdateAsset';
import { useUploadAssetPhoto } from 'src/Hooks/Mutations/useUploadAssetPhoto';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import styles from './AssetSettingsSection.module.scss';

const AssetSettingsSection = () => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: asset } = useFetchAssetDetails();
  const { mutate: updateAsset, isLoading: updateAssetLoading } = useUpdateAsset();

  const [assetName, setAssetName] = useState('');
  const [addressPlaceId, setAddressPlaceId] = useState('');
  const [initialAddressText, setInitialAddressText] = useState('');

  const [photoImagePath, setPhotoImagePath] = useState<string | null>(null);
  const [photoImageUrl, setPhotoImageUrl] = useState<string | null>(null);
  const [isPhotoRemoved, setIsPhotoRemoved] = useState(false);

  const { mutate: uploadPhoto, isLoading: uploadPhotoLoading } = useUploadAssetPhoto({
    onSuccess: data => {
      setPhotoImagePath(data.imagePath);
      setPhotoImageUrl(data.imageUrl);
      setIsPhotoRemoved(false);
    },
  });

  useEffect(() => {
    if (asset?.name) {
      setAssetName(asset.name);
    }
  }, [asset?.name]);

  useEffect(() => {
    if (asset?.address) {
      setAddressPlaceId(asset.address.googlePlaceId ?? '');
      setInitialAddressText(asset.address.formattedAddress ?? '');
    } else {
      setAddressPlaceId('');
      setInitialAddressText('');
    }
  }, [asset?.address]);

  useEffect(() => {
    if (asset) {
      setPhotoImagePath(null);
      setPhotoImageUrl(null);
      setIsPhotoRemoved(false);
    }
  }, [asset?.id]);

  const handleSaveSettings = () => {
    if (!assetId) return;

    const updates: { name?: string; placeId?: string | null; photoImagePath?: string | null } = {};

    if (assetName && assetName !== asset?.name) {
      updates.name = assetName;
    }

    const currentPlaceId = asset?.address?.googlePlaceId ?? '';
    if (addressPlaceId !== currentPlaceId) {
      updates.placeId = addressPlaceId || null;
    }

    if (isPhotoRemoved) {
      updates.photoImagePath = null;
    } else if (photoImagePath) {
      updates.photoImagePath = photoImagePath;
    }

    if (Object.keys(updates).length > 0) {
      updateAsset(
        { assetId, data: updates },
        {
          onSuccess: () => {
            setPhotoImagePath(null);
            setPhotoImageUrl(null);
            setIsPhotoRemoved(false);
          },
        }
      );
    }
  };

  const hasNameChanged = assetName !== '' && assetName !== asset?.name;
  const hasAddressChanged = addressPlaceId !== (asset?.address?.googlePlaceId ?? '');
  const hasPhotoChanged = isPhotoRemoved || photoImagePath !== null;
  const hasChanges = hasNameChanged || hasAddressChanged || hasPhotoChanged;

  if (!assetId) return null;

  return (
    <>
      <PageHeader label={t('admin.assetSettings', 'Paramètres du bien')} />
      <div className={styles.settingsSection}>
        <div className={styles.inputRow}>
          <span className={styles.inputLabel}>{t('admin.renameAsset.label', 'Nom du bien')}</span>
          <TextInput
            className={styles.inputField}
            placeholder={t('admin.renameAsset.placeholder', 'Mon super bien')}
            value={assetName}
            onChange={event => setAssetName(event.currentTarget.value)}
          />
        </div>
        <div className={styles.inputRow}>
          <span className={styles.inputLabel}>{t('admin.address.label', 'Adresse du bien')}</span>
          <AddressAutocomplete
            className={styles.inputField}
            placeholder={t('admin.address.placeholder', 'Rechercher une adresse...')}
            value={addressPlaceId}
            onChange={setAddressPlaceId}
            initialAddressText={initialAddressText}
          />
        </div>
        <div className={styles.inputRow}>
          <span className={styles.inputLabel}>{t('admin.photo.label', 'Photo du bien')}</span>
          <div className={styles.photoSection}>
            {(photoImageUrl ?? (!isPhotoRemoved && asset?.imageUrl)) ? (
              <div className={styles.photoPreview}>
                <img
                  src={photoImageUrl ?? asset?.imageUrl}
                  alt={t('admin.photo.preview', 'Aperçu de la photo')}
                  className={styles.previewImage}
                />
                <ActionIcon
                  className={styles.removePhotoButton}
                  variant="filled"
                  color="red"
                  size="sm"
                  onClick={() => {
                    setPhotoImagePath(null);
                    setPhotoImageUrl(null);
                    setIsPhotoRemoved(true);
                  }}
                >
                  <IconX size={14} />
                </ActionIcon>
              </div>
            ) : (
              <ImageDropzone
                className={styles.photoDropzone}
                onDrop={files => uploadPhoto(files[0])}
                loading={uploadPhotoLoading}
                label={t(
                  'admin.photo.dropzone.label',
                  'Glissez une photo ou cliquez pour parcourir'
                )}
                subtitle={t(
                  'admin.photo.dropzone.subtitle',
                  'Formats acceptés : JPG, PNG. Max 5 MB.'
                )}
              />
            )}
          </div>
        </div>
        <Button
          className={styles.saveButton}
          onClick={handleSaveSettings}
          loading={updateAssetLoading}
          disabled={!hasChanges}
        >
          {t('admin.settings.save', 'Enregistrer les modifications')}
        </Button>
      </div>
    </>
  );
};

export default AssetSettingsSection;
